import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { AppShell } from '@components/layout/AppShell';
import { LoadingSpinner } from '@components/ui/LoadingSpinner';
import { AuthRoute } from '@auth/AuthRoute';
import { RequireRole } from '@auth/RoleContext';

/**
 * App routes. Mirrors the Reports kit's App.tsx structure:
 *  - public /login (+ /login/callback) outside the auth wall
 *  - everything else behind <AuthRoute> (MSAL sign-in) then <AppShell>
 *  - role gating layered on top with <RequireRole> per architecture §4:
 *      Warehouse (or Admin) -> the board + Build Kit Batch action
 *      Admin only           -> config + reports
 *
 * Pages are lazy-loaded (code-split per route), as in the Reports kit.
 */
const Login = lazy(() => import('@pages/Login'));
const WarehouseBoard = lazy(() => import('@pages/WarehouseBoard'));
const AdminConfig = lazy(() => import('@pages/admin/Config'));
const AdminReports = lazy(() => import('@pages/admin/Reports'));
const Forbidden = lazy(() => import('@pages/Forbidden'));
const NotFound = lazy(() => import('@pages/NotFound'));

export function App() {
  return (
    <Suspense fallback={<LoadingSpinner full />}>
      <Routes>
        {/* Public auth routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/login/callback" element={<Login />} />
        <Route path="/logout" element={<Login />} />

        {/* Everything else requires an Entra session */}
        <Route element={<AuthRoute />}>
          <Route element={<AppShell />}>
            <Route index element={<Navigate to="/board" replace />} />

            {/* Warehouse board — Warehouse or Admin */}
            <Route
              path="/board"
              element={
                <RequireRole anyOf={['Warehouse', 'Admin']}>
                  <WarehouseBoard />
                </RequireRole>
              }
            />

            {/* Admin surfaces — Admin only */}
            <Route
              path="/admin/config"
              element={
                <RequireRole anyOf={['Admin']}>
                  <AdminConfig />
                </RequireRole>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <RequireRole anyOf={['Admin']}>
                  <AdminReports />
                </RequireRole>
              }
            />

            {/* Authenticated-but-not-authorized landing */}
            <Route path="/forbidden" element={<Forbidden />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
